import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { PrismButton, PrismSelectInput } from '@mylighthouse/prism-ember';
import type { IconName } from '@mylighthouse/prism-ember/types/components/prism/icon';
import type { Option } from '@mylighthouse/prism-ember/types/components/prism/select-input';
import HotelChannelPricingEditCellPopover from 'lumen/components/hotel/channel-pricing/edit-cell-popover';
import HotelChannelPricingTablesCellsTableCellBase from 'lumen/components/hotel/channel-pricing/tables/cells/table-cell-base';
import HotelChannelPricingTablesCellsTableCellWrapper from 'lumen/components/hotel/channel-pricing/tables/cells/table-cell-wrapper';
import type {
  TdMinorChannelsTableColumnSignature,
  TdMinorChannelsTableRowSignature,
} from 'lumen/components/hotel/channel-pricing/tables/minor-channels-table';
import type { Restriction, RestrictionRecommendation } from 'lumen/models';
import { prepareRestrictionActionPayload } from 'lumen/utils/channel-pricing/prepare-restriction-action-payload';
import {
  RESTRICTION_MINOR_CHANNEL_ID,
  RESTRICTION_MODEL_CONTEXTS,
  RESTRICTION_TYPE_NONE,
  RESTRICTION_TYPES,
  type RestrictionEditOption,
} from 'lumen/utils/constants/channel-pricing-constants';
import type {
  RestrictionActionValue,
  RestrictionType,
} from 'lumen/utils/constants/restriction-actions';
import { differenceInDays } from 'lumen/utils/dates/native-js-date-utils';

import styles from './restrictions-row-cell-content.module.css';

interface HotelChannelPricingTablesCellsMinorChannelsTableRestrictionsRestrictionsRowCellContentSignature {
  Args: {
    column: TdMinorChannelsTableColumnSignature;
    parentCellId: string;
    row: TdMinorChannelsTableRowSignature;
  };
}

export default class HotelChannelPricingTablesCellsMinorChannelsTableRestrictionsRestrictionsRowCellContent extends Component<HotelChannelPricingTablesCellsMinorChannelsTableRestrictionsRestrictionsRowCellContentSignature> {
  @service declare access: Services['access'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service('restrictions/action-poller')
  declare restrictionsActionPoller: Services['restrictions/action-poller'];
  @service('restrictions/data-request-service')
  declare restrictionsDataRequestService: Services['restrictions/data-request-service'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked isCellInEditMode = false;
  @tracked isPopoverOpen = false;
  @tracked selectedOptionId = '';
  popoverElement: HTMLElement | null = null;

  get formattedRestrictionRecommendationValue(): string {
    return (
      this.restrictionRecommendation?.formattedValues[0]?.formattedValue ?? ''
    );
  }

  get hasActionPopover(): boolean {
    return this.hasInteractions && !this.isRestrictionBeingPushedForCurrentDay;
  }

  get hasCellRecommendation(): boolean {
    return (
      !this.args.column.isPastDay &&
      this.hasRestrictionRecommendation &&
      !this.isRestrictionBeingPushedForCurrentDay
    );
  }

  get hasInteractions(): boolean {
    return (
      !this.access.isPricingAssistantActionOrInputDisabled &&
      !this.args.column.isPastDay
    );
  }

  get hasRestriction(): boolean {
    return this.restriction !== undefined;
  }

  get hasRestrictionRecommendation(): boolean {
    return this.restrictionRecommendation !== undefined;
  }

  get hotelLevelRestriction(): Restriction | undefined {
    return this.args.column.hotelLevelRestrictions[0];
  }

  get hotelLevelRestrictionType(): RestrictionType | undefined {
    return this.hotelLevelRestriction?.values[0]?.type;
  }

  get hotelLevelRestrictionValue(): number | undefined {
    return this.hotelLevelRestriction?.values[0]?.value;
  }

  get iconColor(): 'inherit' | 'on-critical' {
    if (this.showException) {
      return 'on-critical';
    }

    return 'inherit';
  }

  get iconName(): 'circle-error-outline' | undefined {
    if (this.showException) {
      return 'circle-error-outline';
    }

    return undefined;
  }

  get isRestrictionBeingPushedForCurrentDay(): boolean {
    return Boolean(
      this.restrictionsActionPoller.datesBeingRefreshed[this.args.column.id] ||
      this.restrictionsDataRequestService.pendingDates[this.args.column.id],
    );
  }

  get isSelectedEditRestrictionSameAsCurrent(): boolean {
    if (
      !this.hasRestriction &&
      this.selectedOption?.type === RESTRICTION_TYPE_NONE
    ) {
      return true;
    }
    if (
      this.restriction?.values?.[0]?.type === this.selectedOption?.type &&
      this.restriction?.values?.[0]?.value === this.selectedOption?.value
    ) {
      return true;
    }
    return false;
  }

  get mainValue(): string {
    if (this.hasRestrictionRecommendation) {
      return this.formattedRestrictionRecommendationValue;
    }

    if (this.hasRestriction) {
      return this.restriction?.formattedValue ?? '';
    }

    return this.intl.t('channel_pricing.restriction.none');
  }

  get mainValueClass(): string | undefined {
    if (this.showException) {
      return;
    }

    if (this.hasRestrictionRecommendation) {
      return styles['intelligence-highlight'];
    }
  }

  get popoverId(): string {
    return `${this.args.parentCellId}-popover`;
  }

  get restriction(): Restriction | undefined {
    return this.args.column.restrictions[0];
  }

  @cached
  get restrictionEditOptions(): RestrictionEditOption[] {
    const baseOptions = [
      {
        id: RESTRICTION_TYPES.CLOSED,
        label: this.intl.t('channel_pricing.restriction.closed'),
        type: RESTRICTION_TYPES.CLOSED,
        value: 1,
      },
    ];

    // if the current hotel level restriction type is CLOSED or OPEN the options will be CLOSED and OPEN
    // otherwise the options will be CLOSED and the current hotel level restriction
    if (
      this.hotelLevelRestrictionType === RESTRICTION_TYPES.MIN_LOS &&
      this.hotelLevelRestrictionValue !== 1
    ) {
      return [
        {
          id: `${RESTRICTION_TYPES.MIN_LOS}${this.restrictionValue}`,
          label: this.intl.t('general.los', {
            los: this.hotelLevelRestrictionValue,
          }),
          type: RESTRICTION_TYPES.MIN_LOS,
          value: this.hotelLevelRestrictionValue,
        },
        ...baseOptions,
      ];
    }

    return [
      {
        id: RESTRICTION_TYPE_NONE,
        label: this.intl.t('channel_pricing.restriction.none'),
        type: RESTRICTION_TYPE_NONE,
        value: undefined,
      },
      ...baseOptions,
    ];
  }

  // SMART DISTRIBUTION TODO: Currently taking first value here for now
  // Maybe later there can be multiple restriction values, but haven't gotten more BE updates yet
  get restrictionRecommendation(): RestrictionRecommendation | undefined {
    return this.args.column.restrictionsRecommendations[0];
  }

  get restrictionsRowActions(): {
    action: (event: Event) => void;
    buttonType: 'primary' | 'secondary';
    disabled?: boolean;
    icon: IconName;
    label?: string;
    name: string;
  }[] {
    if (!this.isCellInEditMode) {
      if (this.hasRestrictionRecommendation) {
        return [
          {
            name: 'triggerEdit',
            buttonType: 'secondary',
            icon: 'edit-pencil',
            // eslint-disable-next-line @typescript-eslint/unbound-method
            action: this.toggleCellEditMode,
          },
          {
            name: 'send',
            buttonType: 'primary',
            icon: 'send',
            // eslint-disable-next-line @typescript-eslint/unbound-method
            action: this.sendRestrictionRecommendation,
          },
        ];
      }

      return [
        {
          name: 'triggerEdit',
          buttonType: 'primary',
          icon: 'edit-pencil',
          // eslint-disable-next-line @typescript-eslint/unbound-method
          action: this.toggleCellEditMode,
        },
      ];
    }

    return [
      {
        name: 'cancelEdit',
        buttonType: 'secondary',
        icon: 'cross',
        // eslint-disable-next-line @typescript-eslint/unbound-method
        action: this.closePopover,
      },
      {
        name: 'send',
        buttonType: 'primary',
        icon: 'send',
        disabled:
          this.selectedOption === undefined ||
          this.isSelectedEditRestrictionSameAsCurrent,
        // eslint-disable-next-line @typescript-eslint/unbound-method
        action: this.sendEditedRestriction,
      },
    ];
  }

  get restrictionType(): RestrictionType | undefined {
    return this.restriction?.values?.[0]?.type;
  }

  get restrictionValue(): number | undefined {
    return this.restriction?.values?.[0]?.value;
  }

  get secondaryValue(): string {
    if (this.hasRestrictionRecommendation) {
      return this.hasRestriction
        ? (this.restriction?.formattedValue ?? '')
        : this.intl.t('channel_pricing.restriction.none');
    }

    return '';
  }

  get selectedOption(): RestrictionEditOption | undefined {
    return this.restrictionEditOptions.find(
      (option) => option.id === this.selectedOptionId,
    );
  }

  get showException(): boolean {
    const dateKey = this.args.column.id;
    if (
      !this.args.column.daysWithFailedRestrictionPush ||
      !this.args.column.daysWithFailedRestrictionPush[dateKey] ||
      !Array.isArray(this.args.column.daysWithFailedRestrictionPush[dateKey])
    ) {
      return false;
    }

    // SMART DISTRIBUTION TODO: Minor channels table exceptions attempt - hardcoded ID. Room for improvement here
    return this.args.column.daysWithFailedRestrictionPush[dateKey].includes(
      RESTRICTION_MINOR_CHANNEL_ID,
    );
  }

  @action
  closePopover(event?: Event): void {
    this.popoverElement?.hidePopover();

    if (event) {
      event.stopPropagation();
    }
  }

  @action
  onChange(option: Option): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    this.selectedOptionId = option['id'];
  }

  @action
  onPopoverToggle(event: ToggleEvent): void {
    this.popoverElement =
      event.newState === 'open' ? (event.target as HTMLElement) : null;
    this.isPopoverOpen = event.newState === 'open';

    if (event.newState === 'open') {
      this.isCellInEditMode = false;

      if (this.hasRestrictionRecommendation) {
        this.selectedOptionId =
          this.restrictionRecommendation?.formattedValues[0]?.valueId || '';
      } else if (this.hasRestriction) {
        this.selectedOptionId =
          this.restriction?.formattedValues[0]?.valueId || '';
      } else {
        this.selectedOptionId = RESTRICTION_TYPE_NONE;
      }
    }
  }

  @action
  sendEditedRestriction(): void {
    if (!this.selectedOption) {
      console.error('No restriction selected');
      return;
    }

    if (!this.selectedOption.type) {
      console.error('No restriction type selected');
      return;
    }

    const restrictionActions = prepareRestrictionActionPayload(
      this.selectedOption.type,
      this.selectedOption.value,
      this.restriction,
    );

    if (restrictionActions !== null) {
      this.sendRestriction(restrictionActions, true);
    }
  }

  @action
  sendRestriction(
    restrictionActions: RestrictionActionValue[],
    isSentFromEditMode = false,
  ): void {
    const restriction_action = {
      from_date: this.args.column.id,
      values: restrictionActions,
      ota_id: RESTRICTION_MINOR_CHANNEL_ID, // Pass hardcoded identifier so BE knows it's an action from the minor channels table
      context: RESTRICTION_MODEL_CONTEXTS.CHANNEL_LEVEL,
    };

    void this.restrictionsDataRequestService.sendRestriction([
      restriction_action,
    ]);

    this.usageAnalytics.track('Restriction Pushed Manually', {
      eventTrigger: 'minor-channels-table',
      product: 'smart-distribution',
      isSentFromEditMode,
      daysOffsetFromToday: differenceInDays(
        this.args.column.day.date.toDate(),
        this.hotelAndMarketService.getLocalTime().toDate(),
      ),
    });

    this.closePopover();
  }

  @action
  sendRestrictionRecommendation(): void {
    const recommendationValues = this.restrictionRecommendation?.values?.[0];

    if (!recommendationValues) {
      console.error('No recommendation values found');
      return;
    }
    const restriction = prepareRestrictionActionPayload(
      recommendationValues.type,
      recommendationValues.value,
      this.restriction,
    );

    if (restriction !== null) {
      this.sendRestriction(restriction);
    }
  }

  @action
  toggleCellEditMode(event: Event): void {
    this.isCellInEditMode = !this.isCellInEditMode;
    event.stopPropagation();
  }

  <template>
    <HotelChannelPricingTablesCellsTableCellWrapper
      @hasInteractions={{this.hasInteractions}}
      @hasOpenCta={{this.isPopoverOpen}}
      @hasRecommendation={{this.hasCellRecommendation}}
      @id={{@parentCellId}}
      @showException={{this.showException}}
      class="edit-cell-popover-button"
      popovertarget={{this.popoverId}}
    >
      <HotelChannelPricingTablesCellsTableCellBase
        @icon={{this.iconName}}
        @iconColor={{this.iconColor}}
        @isLoading={{this.isRestrictionBeingPushedForCurrentDay}}
        @isPriceCell={{false}}
        @main={{this.mainValue}}
        @mainValueClass={{this.mainValueClass}}
        @mainValueHeavyText={{this.hasCellRecommendation}}
        @secondary={{this.secondaryValue}}
        @showException={{this.showException}}
      />

    </HotelChannelPricingTablesCellsTableCellWrapper>

    {{#if this.hasActionPopover}}
      <HotelChannelPricingEditCellPopover
        @onPopoverToggle={{this.onPopoverToggle}}
        @popoverId={{this.popoverId}}
      >
        <div class="flex flex-col items-center gap-200">
          {{#if this.isCellInEditMode}}
            <PrismSelectInput
              @onChange={{this.onChange}}
              @options={{this.restrictionEditOptions}}
              @unsafe_renderInPlace={{true}}
              @value={{this.selectedOption}}
              {{! have renderInPlace to ensure the dropdown is rendered inside the popover (for CTA click behavior)}}
            />
          {{/if}}
          <div class="flex w-full flex-row gap-100">
            {{#each this.restrictionsRowActions as |restrictionsRowAction|}}
              <PrismButton
                @buttonType={{restrictionsRowAction.buttonType}}
                @icon={{restrictionsRowAction.icon}}
                @isDisabled={{restrictionsRowAction.disabled}}
                @label={{restrictionsRowAction.label}}
                @onClick={{restrictionsRowAction.action}}
                @size="small"
              />
            {{/each}}
          </div>
        </div>
      </HotelChannelPricingEditCellPopover>
    {{/if}}
  </template>
}
